import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, UserPlus, Loader2, MoreVertical, Ban, CheckCircle, Trash2, RefreshCw, ChevronLeft, ChevronRight, CalendarCheck, X, GitMerge } from 'lucide-react';
import { getInitials, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import UserDetailModal from '../../components/UserDetailModal';
import { TierBadge, eliteAvatarRing, eliteAvatarFallback } from '../../components/TierBadge';
import { MedicalStaffPanel } from '../../components/admin/MedicalStaffPanel';
import { MergeDuplicatesDialog } from '../../components/admin/MergeDuplicatesDialog';
import { RecentlyDeletedDialog } from '../../components/admin/RecentlyDeletedDialog';

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [learnerTypeFilter, setLearnerTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: 'Welcome@123', role: 'learner', learner_type: 'elite', phone: '', stage: 'prenatal' });
  const [createLoading, setCreateLoading] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [resetBookingFor, setResetBookingFor] = useState(null);
  // V13.14.6 — bulk select + delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [sortBy, setSortBy] = useState('created_at:desc');
  const isSuperadmin = currentUser?.role === 'superadmin';

  const fetchUsers = async () => {
    // Medical Staff tab renders its own panel (MedicalStaffPanel) — no user query.
    if (roleFilter === 'medical') { setLoading(false); return; }
    setLoading(true);
    const params = { search, limit: pageSize, page };
    if (roleFilter !== 'all') params.role = roleFilter;
    if (learnerTypeFilter !== 'all') params.learner_type = learnerTypeFilter;
    if (batchFilter !== 'all') params.batch_id = batchFilter;
    if (courseFilter !== 'all') params.course_id = courseFilter;
    // V14.3 — server-side status filter (no longer client-side post-pagination).
    if (statusFilter !== 'all') params.status = statusFilter;
    // V14.6 — sort
    const [sf, so] = sortBy.split(':');
    params.sort_by = sf;
    params.sort_order = so;
    try {
      const res = await api.get('/api/users', { params });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Reset page + clear selection when filters change
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); setSelectedIds(new Set()); }, 0);
    return () => clearTimeout(id);
  }, [search, roleFilter, learnerTypeFilter, statusFilter, batchFilter, courseFilter, pageSize, sortBy]);

  useEffect(() => {
    const id = setTimeout(() => fetchUsers(), 0);
    api.get('/api/users/counts').then(res => setCounts(res.data)).catch(() => {});
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, learnerTypeFilter, statusFilter, batchFilter, courseFilter, page, pageSize, sortBy]);

  // Fetch batches + courses once for filter dropdowns
  useEffect(() => {
    api.get('/api/batches', { params: { limit: 200 } })
      .then(res => setBatches(res.data.batches || []))
      .catch(() => setBatches([]));
    api.get('/api/courses', { params: { limit: 200 } })
      .then(res => setCourses(res.data.courses || res.data || []))
      .catch(() => setCourses([]));
  }, []);

  const clearAllFilters = () => {
    setLearnerTypeFilter('all');
    setStatusFilter('all');
    setBatchFilter('all');
    setCourseFilter('all');
  };

  // ── Bulk select helpers ──
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const visibleSelectableIds = users
    .filter(u => u.id !== currentUser?.user_id)
    .map(u => u.id);
  const allVisibleSelected = visibleSelectableIds.length > 0
    && visibleSelectableIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleSelectableIds.some(id => selectedIds.has(id))
    && !allVisibleSelected;
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // Remove ALL visible from selection
        const next = new Set(prev);
        visibleSelectableIds.forEach(id => next.delete(id));
        return next;
      }
      // Add ALL visible
      return new Set([...prev, ...visibleSelectableIds]);
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/api/users/bulk-delete', {
        user_ids: Array.from(selectedIds),
      });
      const { deleted_count, skipped, errors, sessions_flagged_for_reassignment, zoom_seats_released } = res.data;
      const parts = [`Deleted ${deleted_count} user${deleted_count === 1 ? '' : 's'}`];
      if (zoom_seats_released > 0) parts.push(`${zoom_seats_released} Zoom seat${zoom_seats_released === 1 ? '' : 's'} released`);
      if (sessions_flagged_for_reassignment > 0) parts.push(`${sessions_flagged_for_reassignment} session${sessions_flagged_for_reassignment === 1 ? '' : 's'} flagged`);
      if ((skipped || []).length) parts.push(`${skipped.length} skipped`);
      if ((errors || []).length) parts.push(`${errors.length} failed`);
      toast.success(parts.join(' · '));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
    setBulkDeleting(false);
  };

  const handleResetBookingQuota = async () => {
    if (!resetBookingFor) return;
    try {
      await api.post(`/api/users/${resetBookingFor.user.id}/reset-booking-quota`, null, {
        params: { date: resetBookingFor.date },
      });
      toast.success(`${resetBookingFor.user.name} can book another class on ${resetBookingFor.date}.`);
      setResetBookingFor(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset booking');
    }
  };

  const handleCreate = async () => {
    if (!createForm.name || !createForm.email) { toast.error('Name and email required'); return; }
    setCreateLoading(true);
    try {
      await api.post('/api/users/create', createForm);
      toast.success('User created!');
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', password: 'Welcome@123', role: 'learner', learner_type: 'elite', phone: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
    setCreateLoading(false);
  };

  // V63.6 — one mother must never sit in the system twice. The merge tool existed but
  // nobody knew to open it, so a duplicate could live for days unnoticed.
  useEffect(() => {
    if (currentUser?.role !== 'superadmin') return;
    api.get('/api/admin/users/duplicates')
      .then((r) => setDupCount(r.data?.total || 0))
      .catch(() => setDupCount(0));
  }, [currentUser?.role, mergeOpen]);

  const showLearnerCols = roleFilter !== 'instructor' && roleFilter !== 'admin';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">{total} total users</p>
        </div>
        <div className="flex items-center gap-2">
          {currentUser?.role === 'superadmin' && roleFilter !== 'medical' && (
            <Button variant="outline" onClick={() => setMergeOpen(true)} data-testid="merge-duplicates-button">
              <GitMerge className="h-4 w-4 mr-2" />Merge duplicates
            </Button>
          )}
          {roleFilter !== 'medical' && (
            <Button variant="outline" onClick={() => setDeletedOpen(true)} data-testid="recently-deleted-button">
              <Trash2 className="h-4 w-4 mr-2" />Recently deleted
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)} data-testid="create-user-button" className={roleFilter === 'medical' ? 'hidden' : ''}>
            <UserPlus className="h-4 w-4 mr-2" />Add User
          </Button>
        </div>
      </div>

      {dupCount > 0 && currentUser?.role === 'superadmin' && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
          data-testid="duplicate-accounts-banner">
          <p className="text-sm text-amber-900">
            <strong>{dupCount}</strong> {dupCount === 1 ? 'person has' : 'people have'} more than one account
            {' '}— same phone number or email. Merging keeps the paid account and moves her history onto it.
          </p>
          <Button size="sm" onClick={() => setMergeOpen(true)} data-testid="duplicate-banner-review-btn">
            Review &amp; merge
          </Button>
        </div>
      )}

      <MergeDuplicatesDialog open={mergeOpen} onClose={() => setMergeOpen(false)} onMerged={() => { fetchUsers(); }} />
      <RecentlyDeletedDialog open={deletedOpen} onClose={() => setDeletedOpen(false)} onRestored={() => { fetchUsers(); }} canRestore={currentUser?.role === 'superadmin'} />

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
            <Tabs value={roleFilter} onValueChange={setRoleFilter} className="flex-1 min-w-[300px]">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="learner">Learners <Badge variant="secondary" className="ml-1 text-[10px]">{counts?.learners?.active || 0}</Badge></TabsTrigger>
                <TabsTrigger value="instructor">Instructors <Badge variant="secondary" className="ml-1 text-[10px]">{counts?.instructors?.total || 0}</Badge></TabsTrigger>
                <TabsTrigger value="medical" data-testid="medical-staff-tab">Medical Staff</TabsTrigger>
                <TabsTrigger value="admin">Admins</TabsTrigger>
              </TabsList>
            </Tabs>
            {roleFilter !== 'medical' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="user-search-input" />
              </div>
            )}
          </div>

          {roleFilter === 'medical' ? (
            <MedicalStaffPanel />
          ) : (
          <>
          {/* Secondary filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
            {showLearnerCols && (
              <>
                <Select value={learnerTypeFilter} onValueChange={setLearnerTypeFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="user-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                    <SelectItem value="trial_paid">Paid Trial</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={batchFilter} onValueChange={setBatchFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="user-batch-filter"><SelectValue placeholder="Batch" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All batches</SelectItem>
                    {batches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={courseFilter} onValueChange={setCourseFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="user-course-filter"><SelectValue placeholder="Course" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All courses</SelectItem>
                    {courses.map(c => (
                      <SelectItem key={c.id || c._id} value={c.id || c._id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="user-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="user-sort-dropdown"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at:desc">Newest first</SelectItem>
                <SelectItem value="created_at:asc">Oldest first</SelectItem>
                <SelectItem value="name:asc">Name A–Z</SelectItem>
                <SelectItem value="name:desc">Name Z–A</SelectItem>
              </SelectContent>
            </Select>
            {(learnerTypeFilter !== 'all' || statusFilter !== 'all' || batchFilter !== 'all' || courseFilter !== 'all') && (
              <Button size="sm" variant="ghost" onClick={clearAllFilters} data-testid="clear-filters-btn">Clear filters</Button>
            )}
          </div>

          {/* Bulk action bar — appears when ≥1 selected */}
          {selectedIds.size > 0 && (
            <div
              className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50/60"
              data-testid="bulk-action-bar"
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-rose-900" data-testid="bulk-selected-count">
                  {selectedIds.size} selected
                </span>
                <Button variant="ghost" size="sm" className="h-7 text-xs"
                        onClick={() => setSelectedIds(new Set())}
                        data-testid="bulk-clear-selection-btn">
                  <X className="h-3 w-3 mr-1" />Clear
                </Button>
              </div>
              {isSuperadmin && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  data-testid="bulk-delete-trigger-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />Delete {selectedIds.size}
                </Button>
              )}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                {isSuperadmin && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                      onCheckedChange={toggleAllVisible}
                      data-testid="bulk-select-all-checkbox"
                      aria-label="Select all visible"
                    />
                  </TableHead>
                )}
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                {showLearnerCols && <TableHead>Type</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={isSuperadmin ? 8 : 7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={isSuperadmin ? 8 : 7} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
              ) : (
                users.map(u => {
                  const tier = u.learner_type;
                  const isSelf = u.id === currentUser?.user_id;
                  return (
                    <TableRow key={u.id} className={selectedIds.has(u.id) ? 'bg-rose-50/30' : ''}>
                      {isSuperadmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(u.id)}
                            onCheckedChange={() => toggleOne(u.id)}
                            disabled={isSelf}
                            data-testid={`bulk-select-row-${u.id}`}
                            aria-label={`Select ${u.name}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <button onClick={() => setDetailUserId(u.id)} className="flex items-center gap-2 text-left hover:underline" data-testid={`user-name-btn-${u.id}`}>
                          <Avatar className={`h-7 w-7 ${u.role === 'learner' ? eliteAvatarRing(tier) : ''}`}>
                            <AvatarFallback className={`text-[10px] ${u.role === 'learner' ? eliteAvatarFallback(tier) : 'bg-primary/10 text-primary'}`}>{getInitials(u.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="font-medium">{u.name}</span>
                            {u.phone && <p className="text-xs text-muted-foreground">{u.phone}</p>}
                          </div>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                      {showLearnerCols && (
                        <TableCell>
                          {u.role === 'learner'
                            ? (tier && tier !== 'free'
                                ? <TierBadge tier={tier} />
                                : <span className="text-xs font-medium text-muted-foreground" data-testid={`user-tier-free-${u.id}`}>Free</span>)
                            : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                      )}
                      <TableCell>
                        {u.is_suspended ? (
                          <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">Suspended</Badge>
                        ) : u.is_active !== false ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-50 text-slate-700">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-3 w-3" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {u.is_suspended ? (
                              <DropdownMenuItem onClick={async () => { await api.post(`/api/users/${u.id}/unsuspend`); toast.success('Unsuspended'); fetchUsers(); }}>
                                <CheckCircle className="h-4 w-4 mr-2" />Unsuspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={async () => { await api.post(`/api/users/${u.id}/suspend`); toast.success('Suspended'); fetchUsers(); }}>
                                <Ban className="h-4 w-4 mr-2" />Suspend
                              </DropdownMenuItem>
                            )}
                            {isSuperadmin && (
                              <DropdownMenuItem className="text-destructive" onClick={async () => {
                                const confirmMsg = u.role === 'instructor'
                                  ? `Delete ${u.name}? This will also release their Zoom Pro seat and flag their upcoming sessions for reassignment.`
                                  : `Delete ${u.name}? This cannot be undone.`;
                                if (window.confirm(confirmMsg)) {
                                  try {
                                    const res = await api.delete(`/api/users/${u.id}`);
                                    const n = res.data?.sessions_needing_reassignment || 0;
                                    const zoom = res.data?.zoom_released;
                                    const parts = ['User deleted'];
                                    if (zoom) parts.push('Zoom seat released');
                                    if (n > 0) parts.push(`${n} session${n === 1 ? '' : 's'} flagged for reassignment`);
                                    toast.success(parts.join(' · '));
                                    fetchUsers();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed');
                                  }
                                }
                              }}>
                                <Trash2 className="h-4 w-4 mr-2" />Delete User
                              </DropdownMenuItem>
                            )}
                            {u.role === 'learner' && (
                              <>
                                <DropdownMenuItem onClick={async () => {
                                  try { await api.post(`/api/users/${u.id}/reset-noshow`); toast.success(`No-show counter reset for ${u.name}`); fetchUsers(); } catch { toast.error('Failed'); }
                                }}>
                                  <RefreshCw className="h-4 w-4 mr-2" />Reset No-Shows {u.no_show_blocked_until ? '(Blocked)' : `(${u.no_show_count || 0})`}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={`reset-booking-quota-${u.id}`}
                                  onClick={() => setResetBookingFor({ user: u, date: new Date().toISOString().slice(0, 10) })}
                                >
                                  <CalendarCheck className="h-4 w-4 mr-2" />Allow re-booking today
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between mt-4 flex-wrap gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Rows per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-20 h-8" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground" data-testid="pagination-info">
                {total === 0 ? '0 of 0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
              </span>
              <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page * pageSize >= total || loading} onClick={() => setPage(p => p + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Add User dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="overflow-visible">
          <DialogHeader><DialogTitle className="font-heading text-xl">Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Full Name</label>
              <Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} data-testid="user-name-input" /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Email</label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} data-testid="user-email-input" /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Phone</label>
              <Input value={createForm.phone} onChange={e => setCreateForm({...createForm, phone: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1.5 block">Role</label>
                <Select
                  value={createForm.role}
                  onValueChange={v => setCreateForm(prev => ({
                    ...prev,
                    role: v,
                    learner_type: v === 'learner' ? (prev.learner_type || 'elite') : null,
                  }))}
                >
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[200]">
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    {currentUser?.role === 'superadmin' && <SelectItem value="admin">Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {createForm.role === 'learner' && (
                <div><label className="text-sm font-medium mb-1.5 block">Learner Type</label>
                  <Select value={createForm.learner_type} onValueChange={v => setCreateForm({...createForm, learner_type: v})}>
                    <SelectTrigger data-testid="user-learner-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent position="popper" sideOffset={4} className="z-[200]">
                      <SelectItem value="elite">Elite Member</SelectItem>
                      <SelectItem value="trial_paid">Paid Trial</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {createForm.role === 'learner' && (
                <div><label className="text-sm font-medium mb-1.5 block">Stage</label>
                  <Select value={createForm.stage || 'prenatal'} onValueChange={v => setCreateForm({...createForm, stage: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent position="popper" sideOffset={4} className="z-[200]">
                      <SelectItem value="prenatal">Prenatal</SelectItem>
                      <SelectItem value="postnatal">Postnatal</SelectItem>
                      <SelectItem value="ttc">TTC (Trying to Conceive)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div><label className="text-sm font-medium mb-1.5 block">Password</label>
              <Input value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})} placeholder="Default: Welcome@123" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createLoading} data-testid="create-user-submit">
              {createLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <UserDetailModal
        userId={detailUserId}
        open={!!detailUserId}
        onClose={() => setDetailUserId(null)}
        onUpdate={fetchUsers}
      />

      {/* Reset booking quota dialog */}
      <Dialog open={!!resetBookingFor} onOpenChange={(v) => !v && setResetBookingFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Allow re-booking on date</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Grant <strong>{resetBookingFor?.user?.name}</strong> a one-time bypass of the &ldquo;1 class per batch per day&rdquo; rule.</p>
            <div>
              <label className="block text-xs font-medium mb-1">Date</label>
              <Input type="date" value={resetBookingFor?.date || ''} onChange={(e) => setResetBookingFor((s) => s ? { ...s, date: e.target.value } : s)} data-testid="reset-booking-date-input" />
            </div>
            <p className="text-xs text-muted-foreground">
              The learner will be able to book <em>one</em> additional session in any of their batches on this date. The bypass auto-consumes on first use.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetBookingFor(null)}>Cancel</Button>
            <Button onClick={handleResetBookingQuota} data-testid="reset-booking-confirm-btn">Grant bypass</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-delete confirm dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(v) => !bulkDeleting && setBulkDeleteOpen(v)}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-rose-700">
              Delete {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              This will permanently delete <strong>{selectedIds.size}</strong> user{selectedIds.size === 1 ? '' : 's'} and cannot be undone.
            </p>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs space-y-1.5">
              <p className="font-medium text-amber-900">Cascade impact</p>
              <ul className="list-disc list-inside text-amber-800 space-y-0.5">
                <li>Instructors: Zoom Pro seat released, upcoming sessions flagged for reassignment</li>
                <li>Learners: Removed from batches, ledger entries retained for audit</li>
                <li>You will be skipped from this batch (cannot self-delete)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Yes, delete {selectedIds.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
